import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
    Table,
    Button,
    Flex,
    Breadcrumb,
    Form,
    Popconfirm,
    Space,
    Pagination,
    Switch,
    TableProps,
} from 'antd';
import dayjs from "dayjs";
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import PIModel from './PIModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import type { ColumnType, SortOrder } from 'antd/es/table/interface';

const useStyle = createStyles(({ css }) => ({
    customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface PerformaInvoice {
    pi_id: number;
    pi_ledger_id: number;
    pi_lead_time: number;
    ledger_first_name : string;
    ledger_last_name: string;
    pi_date: string;
    pi_order: string;
    pi_order_no: string;
    pi_status: number;
}

const PI: React.FC = () => {
    const [invoices, setInvoices] = useState<PerformaInvoice[]>([]);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [initialValues, setInitialValues] = useState<PerformaInvoice | null>(null);
    const { styles } = useStyle();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const currentModule = useCurrentModule();

    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
    const [sortedInfo, setSortedInfo] = useState<any>({});

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const cancel: PopconfirmProps['onCancel'] = () => showError('Cancelled');

    /* ================= Fetch ================= */
    const fetchInvoices = async () => {
        try {
            const data = await apiRequest<PerformaInvoice[]>({
                method: 'get',
                url: '/get-performa-invoices',
            });
            setInvoices(data);
        } catch (error) {
            showError('Failed to fetch invoices');
            console.error(error);
        }
    };

    useEffect(() => {
        fetchInvoices();
    }, []);

    /* ================= Modal ================= */
    const handleEditClick = (record: PerformaInvoice) => {
        setIsEditMode(true);
        setInitialValues(record);
        setIsModalVisible(true);
    };

    const handleCreate = () => fetchInvoices();

    const showCreateModal = () => {
        setIsEditMode(false);
        setInitialValues(null);
        setIsModalVisible(true);
    };

    const hideModal = () => {
        setIsModalVisible(false);
        setIsEditMode(false);
        setInitialValues(null);
    };

    /* ================= Delete ================= */
    const handleDelete = async (pi_id: number) => {
        try {
            await apiRequest({
                method: 'delete',
                url: `/delete-performa-invoice/${pi_id}`,
            });
            showSuccess('Invoice deleted');
            fetchInvoices();
        } catch {
            showError('Failed to delete invoice');
        }
    };

    /* ================= Status Toggle ================= */
    const handleStatusToggle = async (pi_id: number) => {
        setInvoices((prev) =>
            prev.map((item) =>
                item.pi_id === pi_id
                    ? { ...item, pi_status: item.pi_status === 1 ? 0 : 1 }
                    : item
            )
        );

        try {
            await apiRequest({
                method: 'put',
                url: `/update-status-performa-invoice/${pi_id}`,
            });
            showSuccess('Status updated');
        } catch {
            showError('Failed to update status');
            fetchInvoices();
        }
    };

    const handleTableChange: TableProps<PerformaInvoice>['onChange'] = (pagination, filters, sorter) => {
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };

    /* ================= Reusable Text Column ================= */
    const createTextColumn = <T extends Record<string, any>>(
        title: string,
        dataIndex: keyof T,
        data: T[],
        filteredInfo: Record<string, any>,
        sortedInfo: any,
        width?: number
    ): ColumnType<T> => ({
        title,
        dataIndex: dataIndex as string,
        key: dataIndex as string,
        width,
        filters: Array.from(
            new Set(
                data
                    .map((item) => item[dataIndex])
                    .filter((v) => v)
            )
        ).map((value) => ({ text: value, value })),

        filteredValue: filteredInfo[dataIndex as string] ?? null,
        onFilter: (value: any, record: T) =>
            record[dataIndex]?.toString().toLowerCase().includes(value.toString().toLowerCase()),

        sorter: (a: T, b: T) =>
            a[dataIndex]?.toString().localeCompare(b[dataIndex]?.toString()),

        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null,
        sortDirections: ['ascend', 'descend'] as SortOrder[],
        ellipsis: true,
    });

    /* ================= Columns ================= */
    const columns: TableColumnsType<PerformaInvoice> = [
        {
            title: 'PI ID',
            dataIndex: 'pi_id',
            key: 'pi_id',
            sorter: (a, b) => a.pi_id - b.pi_id,
            width:90
        },
        {
            title: 'Order ID',
            dataIndex: 'pi_order_no',
            key: 'pi_order_no',
            width:120
        },
        {
            title: 'Ledger Name',
            key: 'ledger_name',
            width: 200,
            render: (_: any, record: any) =>
                record.ledger_first_name && record.ledger_last_name
                    ? `${record.ledger_first_name} ${record.ledger_last_name}`
                    : '-',
            sorter: (a, b) => {
                const nameA = `${a.ledger_first_name ?? ''} ${a.ledger_last_name ?? ''}`;
                const nameB = `${b.ledger_first_name ?? ''} ${b.ledger_last_name ?? ''}`;
                return nameA.localeCompare(nameB);
            },
        },

        {
            title: 'Date',
            dataIndex: 'pi_date',
            key: 'pi_date',
            render: (date: string) =>
                date ? dayjs(date).format('DD-MMM-YYYY') : '',
        },
        {
            title: 'Lead Time (Days)',
            dataIndex: 'pi_lead_time',
            key: 'pi_lead_time',
            sorter: (a, b) => a.pi_lead_time - b.pi_lead_time,
        },
    ];

    /* ================= Action Column ================= */
    if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
        columns.push({
            title: 'Action',
            width: 220,
            render: (_: any, record: PerformaInvoice) => (
                <Space size="middle">
                    {currentModule?.right_edit === 'Y' && (
                        <>
                            <Switch
                                checkedChildren="Active"
                                unCheckedChildren="Inactive"
                                checked={!!record.pi_status}
                                onChange={() => handleStatusToggle(record.pi_id)}
                            />
                            <Button onClick={() => handleEditClick(record)}>
                                <EditOutlined />
                            </Button>
                        </>
                    )}
                    {currentModule?.right_delete === 'Y' && (
                        <Popconfirm
                            title="Delete this invoice?"
                            onConfirm={() => handleDelete(record.pi_id)}
                            onCancel={cancel}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button danger>
                                <DeleteOutlined />
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        });
    }

    return (
        <>
            <Space
                style={{
                    display: 'flex',
                    marginBottom: 16,
                    justifyContent: 'space-between',
                }}
            >
                <Breadcrumb>
                    <Breadcrumb.Item href="/">
                        <HomeOutlined />
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Performa Invoices</Breadcrumb.Item>
                </Breadcrumb>

                {currentModule?.right_add === 'Y' && (
                    <Button type="primary" onClick={showCreateModal}>
                        Generate PI
                    </Button>
                )}
            </Space>

            <div
                style={{
                    minHeight: 360,
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <Table<PerformaInvoice>
                    className={`${styles.customTable}`}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey="pi_id"
                    dataSource={invoices.slice(
                        (pagination.current - 1) * pagination.pageSize,
                        pagination.current * pagination.pageSize
                    )}
                    bordered
                    pagination={false}
                    locale={{ emptyText: 'No invoice found' }}
                    footer={() => (
                        <Flex justify="space-between">
                            <span>
                                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                                {Math.min(pagination.current * pagination.pageSize, invoices.length)} of{' '}
                                {invoices.length}
                            </span>
                            <Pagination
                                {...pagination}
                                total={invoices.length}
                                showSizeChanger
                                onChange={(page, pageSize) =>
                                    setPagination({ current: page, pageSize })
                                }
                            />
                        </Flex>
                    )}
                />
            </div>

            <PIModel
                visible={isModalVisible}
                onCancel={hideModal}
                onCreate={handleCreate}
                isEditMode={isEditMode}
                initialValues={initialValues}
            />
        </>
    );
};

export default PI;